<template>
    <Upload
        :action="uploadAction"
        :data="formData"
        :show-upload-list="false"
        :on-success="onTemplateUploadSuccess"
        :on-error="onTemplateUploadError"
        :on-progress="showUploadPercentage"
        :multiple="false"
    >
        <Button ref="remote" class="hidden" @click="updatePostData" />
    </Upload>

    <hox-modal
        v-if="showUpdateTemplatePrompt"
        :wrapper-class="replaceTemplateModalWrapperClass"
        @close="updateTemplatePromptCancel"
    >
        <template #header>{{ replaceTemplateModalTitle }}</template>

        <div
            class="template-upload-modal"
            :class="{ 'template-upload-modal--family-selection': replaceTemplateFlow === 'family-selection' }"
        >
            <template v-if="isReplaceTemplateInProgress">
                <p class="small-margin">{{ replaceTemplateProcessingText }}</p>
                <p v-if="replaceTemplatePhaseText" class="small-margin">Current step: {{ replaceTemplatePhaseText }}</p>
                <p class="small-margin">{{ replaceTemplateJobMessage }}</p>
                <p class="template-upload-modal__helper-text">
                    Please do not close this page until the process finishes.
                </p>
                <hox-progress-bar
                    :is-animated="true"
                    :progress-percentage="replaceTemplateProgressPercentage"
                    type="in-progress"
                />
            </template>

            <template v-else-if="replaceTemplateFlow === 'verification'">
                <div v-if="templateUsedInCampaigns?.length">
                    <p class="small-margin">This template is used in the following campaigns:</p>

                    <div class="template-upload-modal__campaigns-list">
                        <div
                            v-for="campaign in templateUsedInCampaigns"
                            :key="campaign._id"
                            class="template-upload-modal__campaign-card"
                        >
                            <span class="template-upload-modal__campaign-icon">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    width="14"
                                    height="14"
                                >
                                    <path
                                        d="M2 4.5A2.5 2.5 0 0 1 4.5 2h11A2.5 2.5 0 0 1 18 4.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 2 15.5v-11Zm2.5-.5a.5.5 0 0 0-.5.5v.5h12v-.5a.5.5 0 0 0-.5-.5h-11ZM16 7H4v8.5a.5.5 0 0 0 .5.5h11a.5.5 0 0 0 .5-.5V7Z"
                                    />
                                </svg>
                            </span>
                            <span class="template-upload-modal__campaign-name">{{ campaign.name }}</span>
                        </div>
                    </div>
                </div>

                <p class="small-margin">{{ updateTemplateModalAdsText }}</p>

                <hox-alert v-if="willResetGeneratedVariants" margin-bottom="none" type="danger" size="small">
                    <template #title>Generated variants will be removed.</template>
                    <template #content>
                        <p class="template-upload-modal__alert-text">
                            The uploaded template changes hoxton.json. If you continue, generated variants for campaigns
                            using this template will be deleted and any published feeds will be reset.
                        </p>
                    </template>
                </hox-alert>

                <hox-alert v-if="hasGroupConflicts" margin-bottom="none" type="danger" size="small">
                    <template #title>The template you are uploading contains different groups.</template>
                    <template #content>
                        <p class="template-upload-modal__alert-text">
                            Any deliverables which are affected by these changes will no longer be accessible. You will
                            lose access to any comments, annotations and status set on these deliverables, and you
                            should rebuild any feeds that have been published.
                        </p>
                        <template v-if="editableGroupConflicts.added.length">
                            <span class="template-upload-modal__label">This template contains these new groups:</span>
                            <ul>
                                <li v-for="added in editableGroupConflicts.added" :key="added" class="capitalize-text">
                                    {{ added }}
                                </li>
                            </ul>
                        </template>
                        <div v-if="editableGroupConflicts.removed.length" class="template-upload-modal__removed">
                            <span class="template-upload-modal__label">
                                This template has removed following groups:
                            </span>
                            <ul>
                                <li
                                    v-for="removed in editableGroupConflicts.removed"
                                    :key="removed"
                                    class="capitalize-text"
                                >
                                    {{ removed }}
                                </li>
                            </ul>
                        </div>
                    </template>
                </hox-alert>

                <hox-alert
                    v-if="templateFamily.length && templateFamilyRootId"
                    margin-bottom="none"
                    type="danger"
                    size="small"
                >
                    <template #title>Template family detected</template>
                    <template #content>
                        <p v-if="templateFamilyMaster" class="template-upload-modal__alert-text">
                            This template belongs to the "{{ templateFamilyMaster.name }}" family. The Master size will
                            be updated first, then the remaining sizes will update automatically one by one. If a size
                            fails, you can retry it manually.
                        </p>
                        <p v-else class="template-upload-modal__alert-text">
                            This template belongs to a template family. The main template will be updated first, then
                            the remaining sizes will update automatically one by one. If a size fails, you can retry it
                            manually.
                        </p>
                        <div class="template-upload-modal__family-size-grid">
                            <div
                                v-if="templateFamilyMaster"
                                class="template-upload-modal__family-size-card template-upload-modal__family-size-card--master"
                            >
                                <span
                                    class="template-upload-modal__family-size-badge template-upload-modal__family-size-badge--master"
                                    >Master</span
                                >
                                <span class="template-upload-modal__family-size-label">
                                    {{ formatTemplateSizeLabel(templateFamilyMaster) }}
                                </span>
                            </div>
                            <div
                                v-for="tpl in templateFamilyResizes"
                                :key="tpl._id"
                                class="template-upload-modal__family-size-card"
                            >
                                <span class="template-upload-modal__family-size-badge">Resize</span>
                                <span class="template-upload-modal__family-size-label">
                                    {{ formatTemplateSizeLabel(tpl) }}
                                </span>
                            </div>
                        </div>
                        <p class="template-upload-modal__alert-text">{{ replaceTemplateFamilyActionText }}</p>
                    </template>
                </hox-alert>
            </template>

            <template v-else-if="replaceTemplateFlow === 'family-selection'">
                <div class="template-upload-modal__family-panel">
                    <div class="template-upload-modal__family-hero">
                        <div>
                            <p class="template-upload-modal__family-eyebrow">Master updated</p>
                            <h4 class="template-upload-modal__family-title">Update the remaining family sizes</h4>
                            <p class="template-upload-modal__family-description">
                                {{ replaceTemplateSuccessMessage }} The remaining sizes are being updated automatically,
                                one by one. If any size fails, you can retry it manually.
                            </p>
                        </div>

                        <div class="template-upload-modal__family-stats">
                            <div class="template-upload-modal__family-stat">
                                <span class="template-upload-modal__family-stat-label">Master</span>
                                <strong class="template-upload-modal__family-stat-value">
                                    {{ templateFamilyMaster ? formatTemplateSizeLabel(templateFamilyMaster) : "" }}
                                </strong>
                            </div>

                            <div class="template-upload-modal__family-stat">
                                <span class="template-upload-modal__family-stat-label">Updated</span>
                                <strong class="template-upload-modal__family-stat-value">
                                    {{ updatedFamilyMemberCount }}/{{ templateFamilyResizes.length }}
                                </strong>
                            </div>

                            <div class="template-upload-modal__family-stat">
                                <span class="template-upload-modal__family-stat-label">Remaining</span>
                                <strong class="template-upload-modal__family-stat-value">
                                    {{ remainingFamilyMemberCount }}
                                </strong>
                            </div>
                        </div>
                    </div>

                    <div class="template-upload-modal__family-scroll dark-scrollbar">
                        <ul class="template-upload-modal__family-actions">
                            <li
                                v-for="template in templateFamilyResizes"
                                :key="template._id"
                                class="template-upload-modal__family-action-row"
                            >
                                <div class="template-upload-modal__family-card">
                                    <div class="template-upload-modal__family-card-main">
                                        <div>
                                            <p class="template-upload-modal__family-card-title">
                                                {{ formatTemplateSizeLabel(template) }}
                                            </p>
                                            <p class="template-upload-modal__family-card-subtitle">
                                                {{ template.name || "Additional family size" }}
                                            </p>
                                        </div>

                                        <span
                                            class="template-upload-modal__family-card-status"
                                            :class="getFamilyMemberStatusClass(template._id)"
                                        >
                                            {{ getFamilyMemberStatusText(template._id) }}
                                        </span>
                                    </div>

                                    <div class="template-upload-modal__family-card-footer">
                                        <Button
                                            type="primary"
                                            :loading="isFamilyMemberLoading(template._id)"
                                            :disabled="
                                                familyMemberStatuses[template._id] === 'success' ||
                                                (familyMemberUpdateInProgress && !isFamilyMemberLoading(template._id))
                                            "
                                            @click="updateFamilyMember(template._id)"
                                        >
                                            {{ getFamilyMemberActionLabel(template._id) }}
                                        </Button>
                                    </div>

                                    <p
                                        v-if="isFamilyMemberFailed(template._id)"
                                        class="template-upload-modal__family-error"
                                    >
                                        {{ getFamilyMemberErrorMessage(template._id) }}
                                    </p>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <p v-if="allFamilyMembersUpdated" class="template-upload-modal__helper-text">
                        All additional family sizes have been updated.
                    </p>
                </div>
            </template>

            <template v-else-if="replaceTemplateFlow === 'success'">
                <p class="small-margin">{{ replaceTemplateSuccessMessage }}</p>
            </template>

            <template v-else-if="replaceTemplateFlow === 'error'">
                <hox-alert margin-bottom="none" type="danger" size="small">
                    <template #title>Template replacement failed.</template>
                    <template #content>
                        <p class="template-upload-modal__alert-text">{{ replaceTemplateErrorMessage }}</p>
                    </template>
                </hox-alert>
            </template>
        </div>

        <template #footer>
            <template v-if="replaceTemplateFlow === 'verification'">
                <Button class="template-upload-modal__button" @click="updateTemplatePromptCancel">Cancel</Button>
                <Button class="template-upload-modal__button" type="primary" @click="updateTemplatePromptOk">
                    {{ replaceTemplatePrimaryActionLabel }}
                </Button>
            </template>
            <template v-else-if="replaceTemplateFlow === 'family-selection'">
                <Button
                    class="template-upload-modal__button"
                    type="primary"
                    :disabled="familyMemberUpdateInProgress"
                    @click="updateTemplatePromptCancel"
                >
                    Close
                </Button>
            </template>
            <template v-else-if="replaceTemplateFlow === 'success'">
                <Button class="template-upload-modal__button" type="primary" @click="updateTemplatePromptCancel">
                    Close
                </Button>
            </template>
            <template v-else-if="replaceTemplateFlow === 'error'">
                <Button class="template-upload-modal__button" @click="updateTemplatePromptCancel">Close</Button>
                <Button class="template-upload-modal__button" type="primary" @click="retryReplaceTemplateAfterError">
                    Retry
                </Button>
            </template>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapStores } from "pinia";

import { USER_ACTIONS } from "shared-utils/enums/user";

import type { Campaign, MasterTemplate } from "@/types/types";

import clientMasterTemplatesUploadConfigQuery from "@/apollo/queries/v2/ClientMasterTemplatesUploadConfig.gql";
import jobByIdQuery from "@/apollo/queries/v2/JobById.gql";

import bus from "@/bus";
import MasterTemplates from "@/services/MasterTemplates";
import { formatTemplateSizeLabel, uploadConfigExpired } from "@/utils";
import { generateJobId, waitForJobToExist } from "@/utils/jobs";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";
import { JobStatus } from "@/enums/jobs";
import { MasterTemplateEvents } from "@/enums/masterTemplate";
import { useMasterTemplatesLibraryStore } from "@/store/pinia/masterTemplatesLibrary";
import { userCan } from "@/services/Auth";

type ReplaceTemplateFlow = "choice" | "processing" | "verification" | "family-selection" | "success" | "error";

type ReplaceTemplateJobPayload = {
    phase?: string;
    force?: boolean;
    progressPercentage?: number;
    usedInCampaigns?: Campaign[];
    manifestHashChanged?: boolean;
    producedAds?: { published: number; unpublished: number };
    editableGroupConflicts?: { added: string[]; removed: string[] };
    templateFamily?: MasterTemplate[];
    errorMessage?: string;
    updatedMasterTemplate?: MasterTemplate;
};

type ReplaceTemplateJob = {
    _id: string;
    message: string;
    status: (typeof JobStatus)[keyof typeof JobStatus];
    payload?: ReplaceTemplateJobPayload;
};

type ReplaceTemplateJobQueryResponse = {
    job?: ReplaceTemplateJob | null;
};

const JOB_POLL_INTERVAL_MS = 5000;
const JOB_POLL_TIMEOUT_MS = 15 * 60 * 1000;
const REPLACE_TEMPLATE_POLL_TIMEOUT_ERROR_MESSAGE =
    "We haven’t received an update for 15 minutes. Something may be wrong. Please try again.";
const FAMILY_MEMBER_POLL_TIMEOUT_ERROR_MESSAGE =
    "We haven’t received an update for this size for 15 minutes. Something may be wrong. Please retry the update.";

export default defineComponent({
    name: "MasterTemplateReplaceButton",
    props: {
        masterTemplateId: {
            type: String,
            default: null
        }
    },

    emits: ["updateStarted", "updated", "familyUpdated"],

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            isLoading: false,
            editableGroupConflicts: { added: [], removed: [] } as { added: string[]; removed: string[] },
            manifestHashChanged: false,
            familyMemberStatuses: {} as Record<string, "idle" | "loading" | "success" | "error">,
            familyMemberErrorMessages: {} as Record<string, string>,
            familyMemberPollTimeoutIds: {} as Record<string, number | undefined>,
            familyMemberPollDeadlineTimeoutIds: {} as Record<string, number | undefined>,
            templateFamily: [] as MasterTemplate[],
            templateFamilyById: {} as Record<string, MasterTemplate>,
            formData: {},
            pendingZipUrl: null as string | null,
            replaceTemplateFlow: "choice" as ReplaceTemplateFlow,
            replaceTemplateForce: false,
            replaceTemplateJobId: null as string | null,
            replaceTemplateRequestJobId: null as string | null,
            replaceTemplateJobPhase: "" as string,
            replaceTemplateJobMessage: "",
            replaceTemplateProgressPercentage: 0,
            replaceTemplateErrorMessage: "",
            replaceTemplateSuccessMessage: "",
            replaceTemplateStarted: false,
            replaceTemplateJobResolve: null as null | ((job: ReplaceTemplateJob) => void),
            replaceTemplateJobReject: null as null | ((error: Error) => void),
            replaceTemplateJobTimeoutId: null as number | null,
            familyMemberAutoUpdateInProgress: false,
            emitUpdatedOnClose: false,
            showUpdateTemplatePrompt: false,
            templateUsedInCampaigns: [] as Campaign[],
            templateUsedProducedAds: {} as { published: number; unpublished: number },
            masterTemplateService: null as null | MasterTemplates
        };
    },

    computed: {
        ...mapStores(useMasterTemplatesLibraryStore),
        userCanManageMasterTemplates() {
            return userCan(USER_ACTIONS.canManageMasterTemplates, { clientId: this.clientId }, false);
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        hasGroupConflicts() {
            return this.editableGroupConflicts.added.length || this.editableGroupConflicts.removed.length;
        },

        willResetGeneratedVariants() {
            return this.manifestHashChanged && this.templateUsedInCampaigns.length > 0;
        },

        templateFamilyRootId() {
            return this.getTemplateFamilyRootId(this.templateFamily);
        },

        templateFamilyMaster() {
            return this.templateFamily.find(({ _id }) => _id === this.templateFamilyRootId) ?? null;
        },

        templateFamilyResizes() {
            return this.templateFamily.filter(({ _id }) => _id !== this.templateFamilyRootId);
        },

        updatedFamilyMemberCount() {
            return this.templateFamilyResizes.filter(({ _id }) => this.familyMemberStatuses[_id] === "success").length;
        },

        remainingFamilyMemberCount() {
            return this.templateFamilyResizes.length - this.updatedFamilyMemberCount;
        },

        allFamilyMembersUpdated() {
            return this.templateFamilyResizes.length > 0
                ? this.templateFamilyResizes.every(({ _id }) => this.familyMemberStatuses[_id] === "success")
                : false;
        },

        familyMemberUpdateInProgress() {
            return Object.values(this.familyMemberStatuses).some(status => status === "loading");
        },

        isReplaceTemplateInProgress() {
            return this.replaceTemplateFlow === "processing";
        },

        replaceTemplateModalWrapperClass() {
            const classes = ["template-upload-modal-wrapper"];

            if (this.replaceTemplateFlow === "processing") {
                classes.push("template-upload-modal-wrapper--hide-close");
            }

            if (this.replaceTemplateFlow === "family-selection") {
                classes.push("template-upload-modal-wrapper--family-selection");
            }

            return classes.join(" ");
        },

        replaceTemplatePrimaryActionLabel() {
            return this.templateFamilyResizes.length > 0 ? "Update master size" : "Replace template";
        },

        replaceTemplatePhaseText() {
            const phase = this.replaceTemplateJobPhase;

            if (!phase) {
                return "";
            }

            const phaseLabels: Record<string, string> = {
                PENDING: "Starting",
                VERIFYING: "Checking impact",
                VERIFICATION_REQUIRED: "Impact ready for review",
                UPDATING: "Updating template",
                COMPLETED: "Completed",
                FAILED: "Failed"
            };

            return phaseLabels[phase] || phase.replaceAll("_", " ").toLowerCase();
        },

        replaceTemplateFamilyActionText() {
            return this.templateFamilyResizes.length > 0
                ? "If you continue, the master size will be updated first. The remaining sizes will then update automatically one by one, and you can manually retry any size that fails."
                : "If you continue, only this template will be replaced.";
        },

        replaceTemplateModalTitle() {
            if (this.replaceTemplateFlow === "success") {
                return "Template updated";
            }

            if (this.replaceTemplateFlow === "error") {
                return "Template replacement failed";
            }

            if (this.replaceTemplateFlow === "processing") {
                return "Replacing template";
            }

            if (this.replaceTemplateFlow === "family-selection") {
                return "Update family sizes";
            }

            return "Replace template";
        },

        replaceTemplateProcessingText() {
            if (
                this.replaceTemplateJobPhase === "PENDING" ||
                this.replaceTemplateJobPhase === "VERIFYING" ||
                this.replaceTemplateJobPhase === "VERIFICATION_REQUIRED"
            ) {
                return "We are checking the template usage";
            }

            if (this.templateFamilyResizes.length > 0) {
                return "We are updating the master size now.";
            }

            return "We are replacing the template now.";
        },

        updateTemplateModalAdsText() {
            const { published = 0, unpublished = 0 } = this.templateUsedProducedAds ?? {};

            const xAds = published + unpublished === 1 ? " ad" : " ads";
            const publishedText = published > 0 ? `${published} published` : "";
            const unpublishedText = unpublished > 0 ? `${unpublished} unpublished` : "";
            const adsText = [publishedText, unpublishedText].filter(text => text).join(" and ");

            if (!adsText) {
                return `This action will affect ads.`;
            }

            return `This action will affect ${adsText}${xAds}.`;
        },

        uploadAction() {
            return this.uploadConfig?.action || "";
        },

        uploadConfig() {
            return this.uploadConfigObject?.config;
        },

        uploadConfigObject() {
            return this.masterTemplatesLibraryStore.uploadConfig;
        },

        templateType() {
            const masterTemplates = this.masterTemplatesLibraryStore.masterTemplates;
            const currentTemplate = this.masterTemplateId ? masterTemplates[this.masterTemplateId] : null;

            if (!currentTemplate) {
                return null;
            }

            return currentTemplate.adType;
        }
    },

    created() {
        /* Trigger the refetching of the upload config in case it has expired. The skip() check
           in the query definition will stop it from being executed if its not needed. */
        this.$apollo.queries.masterTemplateUploadConfig?.refetch?.();

        this.masterTemplateService = new MasterTemplates(
            this.$apollo as unknown as ConstructorParameters<typeof MasterTemplates>[0],
            this.clientId
        );
    },

    beforeUnmount() {
        this.clearAllFamilyMemberPollTimeouts();
        this.clearReplaceTemplateJobPromise();
    },

    methods: {
        formatTemplateSizeLabel,

        getTemplateFamilyRootId(templateFamily: MasterTemplate[] = []) {
            const explicitMaster = templateFamily.find(({ parentId }) => parentId == null);

            if (explicitMaster?._id) {
                return explicitMaster._id;
            }

            const uniqueParentIds = templateFamily.reduce((acc, { parentId }) => {
                if (parentId && !acc.includes(parentId)) {
                    acc.push(parentId);
                }

                return acc;
            }, [] as string[]);

            if (uniqueParentIds.length === 1) {
                return uniqueParentIds[0];
            }

            const currentTemplate = templateFamily.find(({ _id }) => _id === this.masterTemplateId);

            if (currentTemplate?.parentId) {
                return currentTemplate.parentId;
            }

            return currentTemplate?._id ?? null;
        },

        applyTemplateFamily(templateFamily: MasterTemplate[] = []) {
            this.templateFamily = templateFamily;
            const templateFamilyById: Record<string, MasterTemplate> = {};

            templateFamily.forEach(template => {
                templateFamilyById[template._id] = template;
            });

            this.templateFamilyById = templateFamilyById;

            const familyRootId = this.getTemplateFamilyRootId(templateFamily);
            const familyMemberStatuses: Record<string, "idle" | "loading" | "success" | "error"> = {};

            templateFamily
                .filter(({ _id }) => _id !== familyRootId)
                .forEach(({ _id }) => {
                    familyMemberStatuses[_id] = this.familyMemberStatuses[_id] || "idle";
                });

            this.familyMemberStatuses = familyMemberStatuses;
            this.familyMemberErrorMessages = {};
        },

        applyTemplateVerificationPayload(payload: ReplaceTemplateJobPayload = {}) {
            const usedInCampaigns: Campaign[] = payload.usedInCampaigns ?? [];
            const manifestHashChanged = payload.manifestHashChanged ?? false;
            const producedAds = payload.producedAds ?? { published: 0, unpublished: 0 };
            const editableGroupConflicts = payload.editableGroupConflicts ?? { added: [], removed: [] };
            const templateFamily: MasterTemplate[] = payload.templateFamily ?? [];

            this.templateUsedInCampaigns = usedInCampaigns;
            this.manifestHashChanged = manifestHashChanged;
            this.templateUsedProducedAds = producedAds;
            this.editableGroupConflicts = editableGroupConflicts;
            this.applyTemplateFamily(templateFamily);
        },

        clearReplaceTemplateJobPromise() {
            this.clearReplaceTemplateJobTimeout();
            this.replaceTemplateJobResolve = null;
            this.replaceTemplateJobReject = null;
            this.replaceTemplateJobId = null;
            this.replaceTemplateJobPhase = "";
            this.replaceTemplateJobMessage = "";
            this.replaceTemplateProgressPercentage = 0;

            if (this.$apollo.queries.replaceTemplateJob) {
                this.$apollo.queries.replaceTemplateJob.stopPolling();
            }
        },

        clearReplaceTemplateJobTimeout() {
            if (this.replaceTemplateJobTimeoutId) {
                window.clearTimeout(this.replaceTemplateJobTimeoutId);
            }

            this.replaceTemplateJobTimeoutId = null;
        },

        startReplaceTemplateJobTimeout() {
            this.clearReplaceTemplateJobTimeout();

            this.replaceTemplateJobTimeoutId = window.setTimeout(() => {
                const reject = this.replaceTemplateJobReject;

                this.clearReplaceTemplateJobPromise();
                reject?.(new Error(REPLACE_TEMPLATE_POLL_TIMEOUT_ERROR_MESSAGE));
            }, JOB_POLL_TIMEOUT_MS);
        },

        clearFamilyMemberPollTimeout(templateId: string) {
            const timeoutId = this.familyMemberPollTimeoutIds[templateId];

            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }

            this.familyMemberPollTimeoutIds = {
                ...this.familyMemberPollTimeoutIds,
                [templateId]: undefined
            };
        },

        clearFamilyMemberPollDeadlineTimeout(templateId: string) {
            const timeoutId = this.familyMemberPollDeadlineTimeoutIds[templateId];

            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }

            this.familyMemberPollDeadlineTimeoutIds = {
                ...this.familyMemberPollDeadlineTimeoutIds,
                [templateId]: undefined
            };
        },

        clearAllFamilyMemberPollTimeouts() {
            Object.keys(this.familyMemberPollTimeoutIds).forEach(templateId => {
                const timeoutId = this.familyMemberPollTimeoutIds[templateId];

                if (timeoutId) {
                    window.clearTimeout(timeoutId);
                }
            });

            Object.keys(this.familyMemberPollDeadlineTimeoutIds).forEach(templateId => {
                const timeoutId = this.familyMemberPollDeadlineTimeoutIds[templateId];

                if (timeoutId) {
                    window.clearTimeout(timeoutId);
                }
            });

            this.familyMemberPollTimeoutIds = {};
            this.familyMemberPollDeadlineTimeoutIds = {};
        },

        async fetchJobById(jobId: string) {
            const { data } = await this.$apollo.query<ReplaceTemplateJobQueryResponse>({
                fetchPolicy: "no-cache",
                query: jobByIdQuery,
                variables: {
                    jobId
                }
            });

            return data?.job ?? null;
        },

        finalizeFamilyMemberSuccess(templateId: string) {
            this.clearFamilyMemberPollTimeout(templateId);
            this.clearFamilyMemberPollDeadlineTimeout(templateId);
            this.familyMemberStatuses = {
                ...this.familyMemberStatuses,
                [templateId]: "success"
            };
            this.$emit("familyUpdated", [templateId]);

            if (this.allFamilyMembersUpdated) {
                this.$snackbar.success("All family sizes have been updated.");
            }
        },

        finalizeFamilyMemberError(templateId: string, error: unknown) {
            const message = error instanceof Error ? error.message : "Failed to update family size.";

            this.clearFamilyMemberPollTimeout(templateId);
            this.clearFamilyMemberPollDeadlineTimeout(templateId);
            this.familyMemberStatuses = {
                ...this.familyMemberStatuses,
                [templateId]: "error"
            };
            this.familyMemberErrorMessages = {
                ...this.familyMemberErrorMessages,
                [templateId]: message
            };
            this.$snackbar.error("Failed to update family size", message, {
                duration: 0,
                closable: true
            });
        },

        async updateFamilyMembersSequentially() {
            if (this.familyMemberAutoUpdateInProgress || !this.templateFamilyResizes.length) {
                return;
            }

            this.familyMemberAutoUpdateInProgress = true;

            try {
                for (const { _id } of this.templateFamilyResizes) {
                    if (this.familyMemberStatuses[_id] === "success") {
                        continue;
                    }

                    await this.updateFamilyMember(_id);
                }
            } finally {
                this.familyMemberAutoUpdateInProgress = false;
            }
        },

        pollFamilyMemberUpdateJob(templateId: string, jobId: string) {
            return new Promise<void>((resolve, reject) => {
                let settled = false;

                const rejectPoll = (error: unknown) => {
                    if (settled) {
                        return;
                    }

                    settled = true;
                    this.clearFamilyMemberPollTimeout(templateId);
                    this.clearFamilyMemberPollDeadlineTimeout(templateId);
                    reject(error instanceof Error ? error : new Error(FAMILY_MEMBER_POLL_TIMEOUT_ERROR_MESSAGE));
                };

                const resolvePoll = () => {
                    if (settled) {
                        return;
                    }

                    settled = true;
                    this.clearFamilyMemberPollTimeout(templateId);
                    this.clearFamilyMemberPollDeadlineTimeout(templateId);
                    resolve();
                };

                this.clearFamilyMemberPollDeadlineTimeout(templateId);
                this.familyMemberPollDeadlineTimeoutIds = {
                    ...this.familyMemberPollDeadlineTimeoutIds,
                    [templateId]: window.setTimeout(() => {
                        rejectPoll(new Error(FAMILY_MEMBER_POLL_TIMEOUT_ERROR_MESSAGE));
                    }, JOB_POLL_TIMEOUT_MS)
                };

                const poll = async () => {
                    try {
                        const job = await this.fetchJobById(jobId);

                        if (!job || job.status === JobStatus.PENDING || job.status === JobStatus.IN_PROGRESS) {
                            if (job) {
                                this.clearFamilyMemberPollDeadlineTimeout(templateId);
                                this.familyMemberPollDeadlineTimeoutIds = {
                                    ...this.familyMemberPollDeadlineTimeoutIds,
                                    [templateId]: window.setTimeout(() => {
                                        rejectPoll(new Error(FAMILY_MEMBER_POLL_TIMEOUT_ERROR_MESSAGE));
                                    }, JOB_POLL_TIMEOUT_MS)
                                };
                            }

                            this.clearFamilyMemberPollTimeout(templateId);
                            this.familyMemberPollTimeoutIds = {
                                ...this.familyMemberPollTimeoutIds,
                                [templateId]: window.setTimeout(() => {
                                    void poll();
                                }, JOB_POLL_INTERVAL_MS)
                            };
                            return;
                        }

                        if (job.status === JobStatus.FAILED || job.status === JobStatus.ABORTED) {
                            rejectPoll(
                                new Error(job.payload?.errorMessage || job.message || "Failed to update family size.")
                            );
                            return;
                        }

                        resolvePoll();
                    } catch (error) {
                        rejectPoll(error);
                    }
                };

                void poll();
            });
        },

        completeReplaceTemplateModal(message: string) {
            this.ga4.trackFeatureEvent({
                featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                featureCategory: GA4.FEATURE_CATEGORIES.MASTER_TEMPLATES,
                featureContext: GA4.FEATURE_CONTEXTS.UPDATE_TEMPLATE,
                freeformValues: [this.masterTemplateId]
            });

            this.replaceTemplateSuccessMessage = message;
            this.replaceTemplateFlow = "success";
            bus.$emit(MasterTemplateEvents.RefreshMasterTemplateList);
            this.$emit("updated");
            this.$snackbar.success(message);
        },

        handleReplaceTemplateError(error: unknown, title = "Template replacement failed") {
            const message =
                error instanceof Error ? error.message : "An unexpected error occurred while replacing the template.";

            this.replaceTemplateErrorMessage = message;
            this.replaceTemplateFlow = "error";
            this.replaceTemplateRequestJobId = null;
            this.clearReplaceTemplateJobPromise();
            this.$snackbar.error(title, message, {
                duration: 0,
                closable: true
            });
        },

        handleReplaceTemplateJobResult(job: ReplaceTemplateJob) {
            this.replaceTemplateJobPhase = job.payload?.phase ?? this.replaceTemplateJobPhase;
            this.replaceTemplateJobMessage = job.message;
            this.replaceTemplateProgressPercentage =
                job.payload?.progressPercentage ?? this.replaceTemplateProgressPercentage;

            this.startReplaceTemplateJobTimeout();

            if (job.payload?.phase === "UPDATING") {
                this.notifyUpdateStarted();
            }

            if (job.status === JobStatus.PENDING || job.status === JobStatus.IN_PROGRESS) {
                return;
            }

            const resolve = this.replaceTemplateJobResolve;
            const reject = this.replaceTemplateJobReject;
            this.clearReplaceTemplateJobPromise();

            if (job.status === JobStatus.FAILED || job.status === JobStatus.ABORTED) {
                reject?.(new Error(job.payload?.errorMessage || job.message || "Template replacement failed"));
                return;
            }

            resolve?.(job);
        },

        async startReplaceTemplateJob(masterTemplateId: string) {
            if (!this.masterTemplateService) {
                throw new Error("MasterTemplateService not initialized.");
            }

            if (!this.pendingZipUrl) {
                throw new Error("No template upload was found for replacement.");
            }

            const requestedJobId = this.replaceTemplateRequestJobId || generateJobId();

            this.replaceTemplateFlow = "processing";
            this.replaceTemplateForce = true;
            this.replaceTemplateErrorMessage = "";
            this.replaceTemplateSuccessMessage = "";
            this.replaceTemplateJobPhase = "UPDATING";
            this.replaceTemplateJobMessage = "Updating template";
            this.replaceTemplateProgressPercentage = 10;
            this.replaceTemplateRequestJobId = requestedJobId;

            const jobPromise = new Promise<ReplaceTemplateJob>((resolve, reject) => {
                this.replaceTemplateJobResolve = resolve;
                this.replaceTemplateJobReject = reject;
            });

            let jobId = requestedJobId;

            try {
                const { data } = await this.masterTemplateService.update(
                    masterTemplateId,
                    this.pendingZipUrl,
                    this.templateType,
                    true,
                    requestedJobId
                );

                jobId = data?.updateMasterTemplate?.jobId || requestedJobId;
            } catch (error) {
                const jobExists = await waitForJobToExist(this.$apollo, requestedJobId);

                if (!jobExists) {
                    this.clearReplaceTemplateJobPromise();
                    throw error;
                }
            }

            this.replaceTemplateJobId = jobId;
            this.startReplaceTemplateJobTimeout();

            return jobPromise;
        },

        notifyUpdateStarted() {
            if (this.replaceTemplateStarted) {
                return;
            }

            this.replaceTemplateStarted = true;
            this.$emit("updateStarted");
        },

        resetReplaceTemplateModal() {
            this.clearReplaceTemplateJobPromise();
            this.clearAllFamilyMemberPollTimeouts();
            this.editableGroupConflicts = { added: [], removed: [] };
            this.manifestHashChanged = false;
            this.familyMemberStatuses = {};
            this.familyMemberErrorMessages = {};
            this.familyMemberPollTimeoutIds = {};
            this.templateFamily = [];
            this.templateFamilyById = {};
            this.pendingZipUrl = null;
            this.replaceTemplateFlow = "choice";
            this.replaceTemplateForce = false;
            this.replaceTemplateRequestJobId = null;
            this.replaceTemplateErrorMessage = "";
            this.replaceTemplateSuccessMessage = "";
            this.replaceTemplateStarted = false;
            this.familyMemberAutoUpdateInProgress = false;
            this.emitUpdatedOnClose = false;
            this.showUpdateTemplatePrompt = false;
            this.templateUsedInCampaigns = [];
            this.templateUsedProducedAds = { published: 0, unpublished: 0 };
        },

        buildVerificationPayload(
            updateResponse: Partial<ReplaceTemplateJobPayload> | undefined
        ): ReplaceTemplateJobPayload {
            return {
                usedInCampaigns: updateResponse?.usedInCampaigns ?? [],
                manifestHashChanged: updateResponse?.manifestHashChanged ?? false,
                producedAds: updateResponse?.producedAds ?? { published: 0, unpublished: 0 },
                editableGroupConflicts: updateResponse?.editableGroupConflicts ?? { added: [], removed: [] },
                templateFamily: updateResponse?.templateFamily ?? []
            };
        },

        async startReplaceTemplateVerification() {
            try {
                if (!this.masterTemplateService) {
                    throw new Error("MasterTemplateService not initialized.");
                }

                if (!this.pendingZipUrl) {
                    throw new Error("No template upload was found for replacement.");
                }

                const { data } = await this.masterTemplateService.update(
                    this.masterTemplateId,
                    this.pendingZipUrl,
                    this.templateType,
                    false
                );
                const updateResponse = data?.updateMasterTemplate;
                const jobId = updateResponse?.jobId;

                if (!jobId) {
                    throw new Error("No job id returned from updateTemplate mutation");
                }

                const verificationPayload = this.buildVerificationPayload(updateResponse);

                this.replaceTemplateRequestJobId = jobId;
                this.applyTemplateVerificationPayload(verificationPayload);

                // Calculate total ads affected
                const totalAdsAffected =
                    (verificationPayload.producedAds?.published ?? 0) +
                    (verificationPayload.producedAds?.unpublished ?? 0);

                // Show verification modal only if:
                // 1. The template (or its family) is used in at least one campaign, OR
                // 2. There are generated ads affected (published or unpublished)
                if ((verificationPayload.usedInCampaigns?.length ?? 0) > 0 || totalAdsAffected > 0) {
                    this.replaceTemplateFlow = "verification";
                    return;
                }

                await this.updateMasterTemplate();
            } catch (error) {
                this.handleReplaceTemplateError(error);
            }
        },

        async updateTemplate(zipUrl: string) {
            this.resetReplaceTemplateModal();
            this.pendingZipUrl = zipUrl;
            this.replaceTemplateFlow = "processing";
            this.replaceTemplateJobPhase = "VERIFYING";
            this.showUpdateTemplatePrompt = true;
            bus.$emit("percentLoadEvent", 100);
            void this.startReplaceTemplateVerification();
        },

        async updateMasterTemplate() {
            try {
                const targetTemplateId = this.templateFamilyRootId || this.masterTemplateId;

                if (!targetTemplateId) {
                    throw new Error("Unable to determine the template to update.");
                }

                const job = await this.startReplaceTemplateJob(targetTemplateId);

                if (job.payload?.templateFamily?.length) {
                    this.applyTemplateFamily(job.payload.templateFamily);
                }

                if (this.templateFamilyResizes.length > 0) {
                    this.emitUpdatedOnClose = true;
                    this.replaceTemplateSuccessMessage = "Master size updated successfully.";
                    this.replaceTemplateFlow = "family-selection";
                    this.$snackbar.success("Master size updated successfully.");
                    void this.updateFamilyMembersSequentially();
                    return;
                }

                this.completeReplaceTemplateModal("Template Updated");
            } catch (error) {
                this.handleReplaceTemplateError(error);
                return;
            }
        },

        async updateFamilyMember(templateId: string) {
            if (!this.masterTemplateService) {
                return;
            }

            if (!this.templateFamilyRootId) {
                return;
            }

            if (
                (this.familyMemberUpdateInProgress && !this.isFamilyMemberLoading(templateId)) ||
                this.familyMemberStatuses[templateId] === "loading" ||
                this.familyMemberStatuses[templateId] === "success"
            ) {
                return;
            }

            this.familyMemberStatuses = {
                ...this.familyMemberStatuses,
                [templateId]: "loading"
            };
            this.familyMemberErrorMessages = {
                ...this.familyMemberErrorMessages,
                [templateId]: ""
            };
            const requestedJobId = generateJobId();

            try {
                if (!this.masterTemplateService) {
                    throw new Error("MasterTemplateService not initialized.");
                }

                let jobId = requestedJobId;

                try {
                    const { data } = await this.masterTemplateService.updateResize(
                        templateId,
                        this.templateFamilyRootId,
                        requestedJobId
                    );

                    jobId = data?.updateResizedMasterTemplate?.jobId || requestedJobId;
                } catch (error) {
                    const jobExists = await waitForJobToExist(this.$apollo, requestedJobId);

                    if (!jobExists) {
                        throw error;
                    }
                }

                await this.pollFamilyMemberUpdateJob(templateId, jobId);
                this.finalizeFamilyMemberSuccess(templateId);
            } catch (error) {
                this.finalizeFamilyMemberError(templateId, error);
            }
        },

        isFamilyMemberFailed(templateId: string) {
            return this.familyMemberStatuses[templateId] === "error";
        },

        isFamilyMemberLoading(templateId: string) {
            return this.familyMemberStatuses[templateId] === "loading";
        },

        getFamilyMemberActionLabel(templateId: string) {
            if (this.familyMemberStatuses[templateId] === "success") {
                return "Updated";
            }

            if (this.familyMemberStatuses[templateId] === "loading") {
                return "Updating";
            }

            if (this.familyMemberStatuses[templateId] === "error") {
                return "Retry update";
            }

            if (this.familyMemberAutoUpdateInProgress) {
                return "Queued";
            }

            return "Update size";
        },

        getFamilyMemberStatusClass(templateId: string) {
            return `template-upload-modal__family-card-status--${this.familyMemberStatuses[templateId] || "idle"}`;
        },

        getFamilyMemberStatusText(templateId: string) {
            if (this.familyMemberStatuses[templateId] === "success") {
                return "Updated";
            }

            if (this.familyMemberStatuses[templateId] === "loading") {
                return "Updating";
            }

            if (this.familyMemberStatuses[templateId] === "error") {
                return "Needs attention";
            }

            if (this.familyMemberAutoUpdateInProgress) {
                return "Queued";
            }

            return "Ready";
        },

        getFamilyMemberErrorMessage(templateId: string) {
            return this.familyMemberErrorMessages[templateId] || "Failed to update family size.";
        },

        onTemplateUploadError(err: Error) {
            bus.$emit("apolloErrorEvent", err);
        },

        // is called in the MasterTemplateBannerFooterOptions
        // eslint-disable-next-line vue/no-unused-properties
        remoteClick() {
            this.updatePostData();
            (this.$refs.remote as { $el: HTMLElement }).$el.click();
        },

        onTemplateUploadSuccess(_: unknown, file: File) {
            const zipUrl = `${this.uploadConfig?.filePrefix}/${file.name}`;

            void this.updateTemplate(zipUrl);
        },

        showUploadPercentage(e: { percent: number }) {
            bus.$emit("percentLoadEvent", e.percent);
        },

        updatePostData() {
            this.ga4.trackFeatureEvent({
                featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
                featureCategory: GA4.FEATURE_CATEGORIES.MASTER_TEMPLATES,
                featureContext: GA4.FEATURE_CONTEXTS.ADD_TEMPLATE
            });
            // Required to manually add in the Key for some reason.
            if (!this.uploadConfig || !this.uploadConfig.postData) {
                return;
            }

            const data = JSON.parse(this.uploadConfig.postData);
            data.key = `${this.uploadConfig.filePrefix}/\${filename}`;
            this.formData = data;
        },

        updateTemplatePromptCancel() {
            if (this.replaceTemplateFlow === "processing" || this.familyMemberUpdateInProgress) {
                return;
            }

            const shouldEmitUpdated = this.emitUpdatedOnClose;

            this.resetReplaceTemplateModal();

            if (shouldEmitUpdated) {
                bus.$emit(MasterTemplateEvents.RefreshMasterTemplateList);
                this.$emit("updated");
            }
        },

        async retryReplaceTemplateAfterError() {
            if (!this.pendingZipUrl) {
                this.updateTemplatePromptCancel();
                return;
            }

            if (this.replaceTemplateForce) {
                this.replaceTemplateRequestJobId = generateJobId();
                await this.updateMasterTemplate();
                return;
            }

            await this.startReplaceTemplateVerification();
        },

        async updateTemplatePromptOk() {
            this.notifyUpdateStarted();
            await this.updateMasterTemplate();
        }
    },

    apollo: {
        replaceTemplateJob: {
            query: jobByIdQuery,
            fetchPolicy: "no-cache",
            pollInterval: 5000,

            skip() {
                return !this.replaceTemplateJobId;
            },

            variables() {
                return {
                    jobId: this.replaceTemplateJobId
                };
            },

            result({ data, error }: { data?: { job?: ReplaceTemplateJob | null }; error?: Error }) {
                if (error) {
                    return;
                }

                const job = data?.job;

                if (!job) {
                    return;
                }

                this.handleReplaceTemplateJobResult(job);
            }
        },

        masterTemplateUploadConfig: {
            query: clientMasterTemplatesUploadConfigQuery,

            variables() {
                return {
                    clientId: this.clientId
                };
            },

            skip() {
                if (!this.userCanManageMasterTemplates || !this.clientId) {
                    return true;
                }

                if (this.uploadConfigObject?.clientId === this.clientId) {
                    // Check if the one we have is expired or not
                    if (this.uploadConfig && uploadConfigExpired(this.uploadConfig)) {
                        return false;
                    }

                    this.isLoading = false;

                    return true;
                }

                return false;
            },

            manual: true,

            result({ data }) {
                this.masterTemplatesLibraryStore.uploadConfig = {
                    clientId: this.clientId,
                    config: data.masterTemplateUploadConfig
                };

                this.isLoading = false;
            },

            error(e: Error) {
                bus.$emit("apolloErrorEvent", e);
            }
        }
    }
});
</script>
<style lang="scss" scoped>
@use "@style/variables" as *;
.template-upload-modal {
    &--family-selection {
        min-width: 0;
    }

    p.small-margin {
        margin-bottom: $spacing-small;
    }

    &__button {
        margin-left: $spacing-small;
    }

    p.template-upload-modal__alert-text {
        font-size: $font-size-small;
    }

    &__campaigns-list {
        display: flex;
        flex-direction: column;
        gap: 6px;
        max-height: 16rem;
        overflow-y: auto;
        padding-right: 4px;
    }

    &__campaign-card {
        align-items: center;
        background: $white;
        border: 1px solid $grey2;
        border-radius: 10px;
        display: flex;
        gap: $spacing-small;
        padding: $spacing-small $spacing;
        transition: box-shadow 0.15s ease;

        &:hover {
            box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
        }
    }

    &__campaign-icon {
        align-items: center;
        background: rgba(59, 130, 246, 0.1);
        border-radius: 6px;
        color: #1d4ed8;
        display: flex;
        flex-shrink: 0;
        justify-content: center;
        padding: 5px;
    }

    &__campaign-name {
        font-size: $font-size-small;
        font-weight: 500;
        line-height: 1.3;
    }

    &__family-size-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-bottom: $spacing-small;
        margin-top: $spacing-small;
    }

    &__family-size-card {
        align-items: center;
        background: rgba(148, 163, 184, 0.08);
        border: 1px solid $grey2;
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: $spacing-small $spacing;
        text-align: center;

        &--master {
            background: rgba(59, 130, 246, 0.08);
            border-color: rgba(59, 130, 246, 0.3);
        }
    }

    &__family-size-badge {
        background: rgba(148, 163, 184, 0.15);
        border-radius: 999px;
        color: #475569;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.06em;
        padding: 2px 8px;
        text-transform: uppercase;

        &--master {
            background: rgba(59, 130, 246, 0.15);
            color: #1d4ed8;
        }
    }

    &__family-size-label {
        font-size: $font-size-small;
        font-weight: 700;
        letter-spacing: -0.01em;
    }

    &__helper-text {
        font-size: $font-size-small;
        margin-bottom: $spacing-small;
    }

    &__family-panel {
        display: flex;
        flex-direction: column;
        gap: $spacing;
    }

    &__family-hero {
        background: linear-gradient(135deg, rgba(37, 99, 235, 0.08), rgba(15, 23, 42, 0.04));
        border: 1px solid $grey2;
        border-radius: 18px;
        display: flex;
        flex-wrap: wrap;
        gap: $spacing;
        justify-content: space-between;
        padding: $spacing-large;
    }

    &__family-eyebrow {
        color: $primary-color;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.08em;
        margin: 0 0 $spacing-smallest;
        text-transform: uppercase;
    }

    &__family-title {
        font-size: $font-size-larger;
        line-height: 1.2;
        margin: 0 0 $spacing-smallest;
    }

    &__family-description {
        color: $grey5;
        font-size: $font-size-small;
        line-height: 1.5;
        margin: 0;
        max-width: 34rem;
    }

    &__family-stats {
        display: grid;
        gap: $spacing-small;
        grid-template-columns: repeat(3, minmax(100px, 1fr));
        min-width: min(100%, 320px);
    }

    &__family-stat {
        background: rgba(255, 255, 255, 0.85);
        border: 1px solid $grey2;
        border-radius: 14px;
        padding: $spacing-small;
    }

    &__family-stat-label {
        color: $grey5;
        display: block;
        font-size: 11px;
        margin-bottom: $spacing-smallest;
        text-transform: uppercase;
    }

    &__family-stat-value {
        display: block;
        font-size: $font-size-base;
        line-height: 1.2;
    }

    &__family-scroll {
        max-height: min(50vh, 28rem);
        overflow-y: auto;
        padding-right: $spacing-smallest;
    }

    &__family-actions {
        display: grid;
        gap: $spacing-small;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        margin: 0;
        padding: 0;
    }

    &__family-action-row {
        list-style: none;
        margin-left: 0;
        margin-bottom: 0;
    }

    &__family-card {
        background: $white;
        border: 1px solid $grey2;
        border-radius: 18px;
        box-shadow: 0 16px 30px rgba(15, 23, 42, 0.06);
        display: flex;
        flex-direction: column;
        gap: $spacing-small;
        min-height: 100%;
        padding: $spacing;
    }

    &__family-card-main {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: $spacing-small;
    }

    &__family-card-title {
        font-size: $font-size-large;
        font-weight: 700;
        margin: 0;
    }

    &__family-card-subtitle {
        color: $grey5;
        font-size: $font-size-small;
        line-height: 1.4;
        margin: $spacing-smallest 0 0;
    }

    &__family-card-status {
        align-self: flex-start;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 700;
        line-height: 1;
        padding: 8px 10px;
        white-space: nowrap;

        &--idle {
            background: rgba(148, 163, 184, 0.12);
            color: #475569;
        }

        &--loading {
            background: rgba(59, 130, 246, 0.12);
            color: #1d4ed8;
        }

        &--success {
            background: rgba(16, 185, 129, 0.12);
            color: #047857;
        }

        &--error {
            background: rgba(239, 68, 68, 0.12);
            color: #b91c1c;
        }
    }

    &__family-card-footer {
        display: flex;
        justify-content: flex-end;
        margin-top: auto;
    }

    &__family-error {
        color: $error-color;
        font-size: $font-size-small;
        margin-bottom: 0;
        margin-top: $spacing-smallest;
    }

    &__label {
        font-size: $font-size-small;
    }

    ul {
        margin-bottom: $spacing-small;
    }

    li {
        font-size: $font-size-small;
        margin-left: $spacing-large;
    }

    &__removed {
        margin-top: $spacing-small;
    }

    @media (max-width: 768px) {
        &__family-hero {
            padding: $spacing;
        }

        &__family-stats {
            grid-template-columns: 1fr;
            min-width: 100%;
        }

        &__family-actions {
            grid-template-columns: 1fr;
        }

        &__family-scroll {
            max-height: min(48vh, 24rem);
        }
    }
}
</style>

<style lang="scss">
@use "@style/variables" as *;

.template-upload-modal-wrapper--hide-close .hox-modal__close-icon-container {
    display: none;
}

.template-upload-modal-wrapper--family-selection {
    .hox-modal {
        width: min(920px, calc(100vw - #{$spacing-large * 2}));
    }

    .hox-modal__body {
        overflow: hidden;
        padding: $spacing-large;
    }

    .hox-modal__footer {
        border-top: 1px solid $grey2;
        padding-top: $spacing;
    }
}
</style>
